import React, { useEffect, useState } from "react";
import { pdf } from "@react-pdf/renderer";

import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Layout,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  Popconfirm,
} from "antd";

import {
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  DollarOutlined,
  BarChartOutlined,
} from "@ant-design/icons";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from "recharts";

import Swal from "sweetalert2";

import api from "../api/api";
import { useAcademicYear } from "../AcademicYearContext";
import InformationSheetPDF from "./InformationSheetPDF";

const { Title, Text } = Typography;
const { Option } = Select;

const ORANGE = "#ff7900";
const BLACK = "#111111";

/*
|--------------------------------------------------------------------------
| STUDENT TYPES
|--------------------------------------------------------------------------
*/

const STUDENT_TYPES = [
  {
    value: "new",
    label: "New Student",
  },
  {
    value: "old",
    label: "Old Student",
  },
  {
    value: "scholarship",
    label: "Scholarship Student",
  },
  {
    value: "ward",
    label: "Ward Student",
  },
];

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const SchoolFeesData = () => {
  const [form] = Form.useForm();

  const [gradeClasses, setGradeClasses] = useState([]);
  const [feesData, setFeesData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(4);

  const [editData, setEditData] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const { selectedYear } = useAcademicYear();

  /*
  |--------------------------------------------------------------------------
  | LOAD DATA
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (!selectedYear) {
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);

        /*
        |--------------------------------------------------------------------------
        | LOAD GRADE CLASSES
        |--------------------------------------------------------------------------
        */

        const classRes = await api.get(
          "/api/users/grade-classes/"
        );

        const classes =
          classRes.data.results ||
          classRes.data ||
          [];

        setGradeClasses(classes);

        /*
        |--------------------------------------------------------------------------
        | LOAD SCHOOL FEES
        |--------------------------------------------------------------------------
        */

        const feesRes = await api.get(
          "/api/users/school-fees-data/",
          {
            params: {
              academic_year: selectedYear.id,
            },
          }
        );

        const results =
          feesRes.data.results ||
          feesRes.data ||
          [];

        /*
        |--------------------------------------------------------------------------
        | MAP GRADE CLASS
        |--------------------------------------------------------------------------
        */

        const mapped = results.map((item) => {
          const matchedClass = classes.find(
            (cls) =>
              cls.id === item.grade_class
          );

          return {
            ...item,

            grade_class:
              matchedClass ||
              item.grade_class,
          };
        });

        setFeesData(mapped);
        setFilteredData(mapped);
      } catch (err) {
        console.error(
          "Error loading school fees data:",
          err
        );

        Swal.fire(
          "Error",
          "Failed to load initial data.",
          "error"
        );
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedYear]);

  /*
  |--------------------------------------------------------------------------
  | SEARCH
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const search =
      searchText.trim().toLowerCase();

    if (!search) {
      setFilteredData(feesData);
      setPage(1);
      return;
    }

    const filtered = feesData.filter(
      (item) => {
        const gradeClass =
          item.grade_class?.name ||
          "";

        const studentType =
          item.student_type ||
          "";

        return (
          gradeClass
            .toLowerCase()
            .includes(search) ||
          studentType
            .toLowerCase()
            .includes(search)
        );
      }
    );

    setFilteredData(filtered);
    setPage(1);
  }, [feesData, searchText]);

  /*
  |--------------------------------------------------------------------------
  | DOWNLOAD INFORMATION SHEET
  |--------------------------------------------------------------------------
  */

  const downloadInformationSheet =
    async () => {
      try {
        const blob = await pdf(
          <InformationSheetPDF
            fees={filteredData}
            academicYear={selectedYear}
          />
        ).toBlob();

        const url =
          URL.createObjectURL(blob);

        const link =
          document.createElement("a");

        link.href = url;

        link.download =
          "Class_Fees_Information_Sheet.pdf";

        document.body.appendChild(link);

        link.click();

        document.body.removeChild(link);

        URL.revokeObjectURL(url);
      } catch (error) {
        console.error(error);

        Swal.fire(
          "Error",
          "Unable to generate information sheet.",
          "error"
        );
      }
    };

  /*
  |--------------------------------------------------------------------------
  | INSTALLMENT CALCULATION
  |--------------------------------------------------------------------------
  */

  const handleInstallmentChange = (
    field,
    value
  ) => {
    const values =
      form.getFieldsValue();

    const yearly =
      Number(
        values.yearly_school_fee
      ) || 0;

    const registration =
      Number(
        field === "registration"
          ? value
          : values.registration
      ) || 0;

    const first =
      Number(
        field === "first_installment"
          ? value
          : values.first_installment
      ) || 0;

    const second =
      Number(
        field === "second_installment"
          ? value
          : values.second_installment
      ) || 0;

    const third =
      Number(
        field === "third_installment"
          ? value
          : values.third_installment
      ) || 0;

    const remaining =
      yearly -
      registration -
      first -
      second -
      third;

    form.setFieldsValue({
      [field]: value,

      fourth_installment:
        remaining >= 0
          ? Number(remaining.toFixed(2))
          : 0,
    });
  };

  /*
  |--------------------------------------------------------------------------
  | OPEN CREATE MODAL
  |--------------------------------------------------------------------------
  */

  const openCreateModal = () => {
    setEditData(null);

    form.resetFields();

    setModalOpen(true);
  };

  /*
  |--------------------------------------------------------------------------
  | EDIT
  |--------------------------------------------------------------------------
  */

  const handleEdit = (data) => {
    setEditData(data);

    form.setFieldsValue({
      grade_class:
        data.grade_class?.id ||
        data.grade_class,

      student_type:
        data.student_type,

      yearly_school_fee:
        data.yearly_school_fee,

      registration:
        data.registration,

      first_installment:
        data.first_installment,

      second_installment:
        data.second_installment,

      third_installment:
        data.third_installment,

      fourth_installment:
        data.fourth_installment,
    });

    setModalOpen(true);
  };

  /*
  |--------------------------------------------------------------------------
  | CLOSE MODAL
  |--------------------------------------------------------------------------
  */

  const closeModal = () => {
    setModalOpen(false);

    setEditData(null);

    form.resetFields();
  };

  /*
  |--------------------------------------------------------------------------
  | SUBMIT
  |--------------------------------------------------------------------------
  */

  const handleSubmit = async (
    values
  ) => {
    if (!selectedYear) {
      Swal.fire(
        "Select Academic Year",
        "Please select an academic year first.",
        "warning"
      );

      return;
    }

    setLoading(true);

    try {
      /*
      |--------------------------------------------------------------------------
      | YEARLY FEE
      |--------------------------------------------------------------------------
      */

      const yearly =
        Number(
          values.yearly_school_fee
        ) || 0;

      /*
      |--------------------------------------------------------------------------
      | INSTALLMENTS
      |--------------------------------------------------------------------------
      */

      const totalInstallments =
        Number(
          values.registration || 0
        ) +
        Number(
          values.first_installment || 0
        ) +
        Number(
          values.second_installment || 0
        ) +
        Number(
          values.third_installment || 0
        ) +
        Number(
          values.fourth_installment || 0
        );

      /*
      |--------------------------------------------------------------------------
      | VALIDATE INSTALLMENTS
      |--------------------------------------------------------------------------
      */

      if (
        Math.abs(
          totalInstallments -
            yearly
        ) > 0.01
      ) {
        Swal.fire(
          "Validation Error",
          `Total installments (${totalInstallments.toFixed(
            2
          )}) must equal yearly fee (${yearly.toFixed(
            2
          )}).`,
          "error"
        );

        setLoading(false);

        return;
      }

      /*
      |--------------------------------------------------------------------------
      | PAYLOAD
      |--------------------------------------------------------------------------
      */

      const payload = {
        academic_year:
          selectedYear.id,

        grade_class:
          values.grade_class,

        student_type:
          values.student_type,

        yearly_school_fee:
          values.yearly_school_fee,

        registration:
          values.registration,

        first_installment:
          values.first_installment,

        second_installment:
          values.second_installment,

        third_installment:
          values.third_installment,

        fourth_installment:
          values.fourth_installment,
      };

      /*
      |--------------------------------------------------------------------------
      | UPDATE
      |--------------------------------------------------------------------------
      */

      if (editData) {
        const res =
          await api.put(
            `/api/users/school-fees-data/${editData.id}/`,
            payload
          );

        const selectedClass =
          gradeClasses.find(
            (cls) =>
              cls.id ===
              values.grade_class
          );

        const updatedData = {
          ...res.data,

          grade_class:
            selectedClass ||
            res.data.grade_class,
        };

        const updatedFees =
          feesData.map((item) =>
            item.id ===
            editData.id
              ? updatedData
              : item
          );

        setFeesData(updatedFees);

        Swal.fire(
          "Updated!",
          "Fee structure updated successfully.",
          "success"
        );
      }

      /*
      |--------------------------------------------------------------------------
      | CREATE
      |--------------------------------------------------------------------------
      */

      else {
        const exists =
          feesData.some(
            (item) =>
              (
                item.grade_class?.id ||
                item.grade_class
              ) ===
                payload.grade_class &&
              String(
                item.student_type
              ).toLowerCase() ===
                String(
                  payload.student_type
                ).toLowerCase()
          );

        if (exists) {
          const selectedType =
            STUDENT_TYPES.find(
              (type) =>
                type.value ===
                payload.student_type
            );

          Swal.fire(
            "Duplicate Fee",
            `This grade class already has a fee structure for ${
              selectedType?.label ||
              payload.student_type
            }.`,
            "warning"
          );

          setLoading(false);

          return;
        }

        const res =
          await api.post(
            "/api/users/school-fees-data/",
            payload
          );

        const selectedClass =
          gradeClasses.find(
            (cls) =>
              cls.id ===
              values.grade_class
          );

        const updatedData = {
          ...res.data,

          grade_class:
            selectedClass ||
            res.data.grade_class,
        };

        setFeesData([
          updatedData,
          ...feesData,
        ]);

        Swal.fire(
          "Created!",
          "Fee structure created successfully.",
          "success"
        );
      }

      closeModal();
    } catch (error) {
      console.error(
        "Error saving school fees data:",
        error.response?.data ||
          error
      );

      const backendMessage =
        error.response?.data
          ?.detail ||
        error.response?.data
          ?.student_type ||
        error.response?.data
          ?.error;

      Swal.fire(
        "Error",
        backendMessage ||
          "Failed to save fee structure.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | DELETE
  |--------------------------------------------------------------------------
  */

  const handleDelete = async (
    id
  ) => {
    try {
      await api.delete(
        `/api/users/school-fees-data/${id}/`
      );

      const updatedFees =
        feesData.filter(
          (item) =>
            item.id !== id
        );

      setFeesData(updatedFees);

      Swal.fire(
        "Deleted!",
        "The fee record has been deleted.",
        "success"
      );
    } catch (error) {
      console.error(
        "Error deleting data:",
        error
      );

      Swal.fire(
        "Error",
        "Failed to delete record.",
        "error"
      );
    }
  };

  /*
  |--------------------------------------------------------------------------
  | HELPERS
  |--------------------------------------------------------------------------
  */

  const money = (value) =>
    `$${Number(
      value || 0
    ).toFixed(2)}`;

  const getStudentTypeLabel = (
    type
  ) => {
    const normalized =
      String(
        type || ""
      ).toLowerCase();

    const found =
      STUDENT_TYPES.find(
        (item) =>
          item.value ===
          normalized
      );

    return (
      found?.label ||
      `${normalized} Student`
    );
  };

  /*
  |--------------------------------------------------------------------------
  | STATISTICS
  |--------------------------------------------------------------------------
  */

  const totalRecords =
    filteredData.length;

  const totalFees =
    filteredData.reduce(
      (sum, item) =>
        sum +
        Number(
          item.yearly_school_fee ||
            0
        ),
      0
    );

  /*
  |--------------------------------------------------------------------------
  | TABLE COLUMNS
  |--------------------------------------------------------------------------
  */

  const columns = [
    /*
    |--------------------------------------------------------------------------
    | GRADE CLASS
    |--------------------------------------------------------------------------
    */

    {
      title: "GRADE CLASS",

      dataIndex: [
        "grade_class",
        "name",
      ],

      key: "grade_class",

      fixed: "left",

      width: 170,

      render: (text) => (
        <Text strong>
          {text || "N/A"}
        </Text>
      ),
    },

    /*
    |--------------------------------------------------------------------------
    | STUDENT TYPE
    |--------------------------------------------------------------------------
    */

    {
      title: "STUDENT TYPE",

      dataIndex: "student_type",

      key: "student_type",

      width: 190,

      render: (type) => {
        const normalizedType =
          String(
            type || ""
          ).toLowerCase();

        const isScholarship =
          normalizedType ===
          "scholarship";

        const isWard =
          normalizedType ===
          "ward";

        const isNew =
          normalizedType ===
          "new";

        return (
          <Tag
            style={{
              fontWeight: 700,

              textTransform:
                "uppercase",

              fontSize:
                isScholarship ||
                isWard
                  ? "11px"
                  : "12px",

              color:
                isScholarship ||
                isWard
                  ? "#ffffff"
                  : "#111111",

              background:
                isScholarship
                  ? "#001f5b"
                  : isWard
                  ? "#16a34a"
                  : isNew
                  ? ORANGE
                  : "#e5e7eb",

              borderColor:
                isScholarship
                  ? "#001f5b"
                  : isWard
                  ? "#16a34a"
                  : "transparent",

              padding:
                isScholarship ||
                isWard
                  ? "3px 8px"
                  : "4px 10px",

              borderRadius: 6,

              whiteSpace:
                "nowrap",
            }}
          >
            {getStudentTypeLabel(
              type
            )}
          </Tag>
        );
      },
    },

    /*
    |--------------------------------------------------------------------------
    | YEARLY FEE
    |--------------------------------------------------------------------------
    */

    {
      title: "YEARLY FEE",

      dataIndex:
        "yearly_school_fee",

      key:
        "yearly_school_fee",

      width: 140,

      render: money,
    },

    /*
    |--------------------------------------------------------------------------
    | REGISTRATION
    |--------------------------------------------------------------------------
    */

    {
      title: "REGISTRATION",

      dataIndex:
        "registration",

      key:
        "registration",

      width: 150,

      render: money,
    },

    /*
    |--------------------------------------------------------------------------
    | FIRST INSTALLMENT
    |--------------------------------------------------------------------------
    */

    {
      title: "1ST INSTALLMENT",

      dataIndex:
        "first_installment",

      key:
        "first_installment",

      width: 150,

      render: money,
    },

    /*
    |--------------------------------------------------------------------------
    | SECOND INSTALLMENT
    |--------------------------------------------------------------------------
    */

    {
      title: "2ND INSTALLMENT",

      dataIndex:
        "second_installment",

      key:
        "second_installment",

      width: 150,

      render: money,
    },

    /*
    |--------------------------------------------------------------------------
    | THIRD INSTALLMENT
    |--------------------------------------------------------------------------
    */

    {
      title: "3RD INSTALLMENT",

      dataIndex:
        "third_installment",

      key:
        "third_installment",

      width: 150,

      render: money,
    },

    /*
    |--------------------------------------------------------------------------
    | FOURTH INSTALLMENT
    |--------------------------------------------------------------------------
    */

    {
      title: "4TH INSTALLMENT",

      dataIndex:
        "fourth_installment",

      key:
        "fourth_installment",

      width: 150,

      render: money,
    },

    /*
    |--------------------------------------------------------------------------
    | ACTIONS
    |--------------------------------------------------------------------------
    */

    {
      title: "ACTIONS",

      key: "actions",

      fixed: "right",

      width: 150,

      render: (_, record) => (
        <Space>
          <Tooltip title="Edit Record">
            <Button
              type="primary"
              icon={
                <EditOutlined />
              }
              onClick={() =>
                handleEdit(record)
              }
              style={{
                background:
                  BLACK,
                borderColor:
                  BLACK,
              }}
            />
          </Tooltip>

          <Popconfirm
            title="Delete this fee record?"
            description="This action cannot be undone."
            onConfirm={() =>
              handleDelete(
                record.id
              )
            }
            okText="Delete"
            cancelText="Cancel"
          >
            <Tooltip title="Delete Record">
              <Button
                danger
                icon={
                  <DeleteOutlined />
                }
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  /*
  |--------------------------------------------------------------------------
  | CHART DATA
  |--------------------------------------------------------------------------
  */

  const chartData =
    filteredData.map(
      (item) => ({
        name:
          item.grade_class
            ?.name ||
          "Unknown",

        fee: Number(
          item.yearly_school_fee ||
            0
        ),
      })
    );

  /*
  |--------------------------------------------------------------------------
  | STUDENT TYPE SELECT
  |--------------------------------------------------------------------------
  */

const StudentTypeSelect = ({
  value,
  onChange,
  size = "large",
}) => (
  <Select
    value={value}
    onChange={onChange}
    placeholder="Select student type"
    size={size}
    allowClear
  >
    {STUDENT_TYPES.map((type) => (
      <Option
        key={type.value}
        value={type.value}
      >
        {type.label}
      </Option>
    ))}
  </Select>
);

  /*
  |--------------------------------------------------------------------------
  | UI
  |--------------------------------------------------------------------------
  */

  return (
    <Layout
      style={{
        minHeight: "100vh",

        background:
          "linear-gradient(135deg, #f5f5f5 0%, #ffffff 50%, #fff3e8 100%)",
      }}
    >
      <div
        style={{
          padding:
            "clamp(16px, 3vw, 32px)",

          maxWidth: 1700,

          margin: "0 auto",

          width: "100%",
        }}
      >
        {/* =========================================================
            HEADER
        ========================================================= */}

        <Card
          bordered={false}
          style={{
            marginBottom: 24,

            borderRadius: 20,

            overflow: "hidden",

            background:
              "linear-gradient(135deg, #111111 0%, #242424 60%, #ff7900 160%)",

            boxShadow:
              "0 15px 40px rgba(0,0,0,0.16)",
          }}
        >
          <Row
            align="middle"
            justify="space-between"
            gutter={[
              20,
              20,
            ]}
          >
            <Col
              xs={24}
              md={16}
            >
              <Space
                direction="vertical"
                size={4}
              >
                <Text
                  style={{
                    color:
                      ORANGE,

                    fontWeight:
                      800,

                    letterSpacing:
                      2,
                  }}
                >
                  FINANCE MANAGEMENT
                </Text>

                <Title
                  level={2}
                  style={{
                    color:
                      "#ffffff",

                    margin: 0,

                    fontSize:
                      "clamp(24px, 4vw, 38px)",
                  }}
                >
                  School Fees Management
                </Title>

                <Text
                  style={{
                    color:
                      "#d9d9d9",

                    fontSize: 15,
                  }}
                >
                  Configure and
                  manage student
                  fee structures
                  by academic
                  year.
                </Text>
              </Space>
            </Col>

            <Col
              xs={24}
              md={8}
            >
              <div
                style={{
                  textAlign:
                    window.innerWidth <
                    768
                      ? "left"
                      : "right",
                }}
              >
                <Text
                  style={{
                    color:
                      "#bbbbbb",

                    display:
                      "block",
                  }}
                >
                  ACTIVE ACADEMIC YEAR
                </Text>

                <Title
                  level={3}
                  style={{
                    color:
                      ORANGE,

                    margin:
                      "4px 0 0",
                  }}
                >
                  {selectedYear
                    ?.name ||
                    "None Selected"}
                </Title>
              </div>
            </Col>
          </Row>
        </Card>

        {/* =========================================================
            STATISTICS
        ========================================================= */}

        <Row
          gutter={[
            16,
            16,
          ]}
          style={{
            marginBottom: 24,
          }}
        >
          <Col
            xs={24}
            sm={12}
            lg={8}
          >
            <Card
              bordered={false}
              style={{
                borderRadius: 16,

                borderLeft:
                  `5px solid ${ORANGE}`,

                boxShadow:
                  "0 8px 25px rgba(0,0,0,0.08)",
              }}
            >
              <Statistic
                title="Fee Records"
                value={
                  totalRecords
                }
                prefix={
                  <BarChartOutlined
                    style={{
                      color:
                        ORANGE,
                    }}
                  />
                }
              />
            </Card>
          </Col>

          <Col
            xs={24}
            sm={12}
            lg={8}
          >
            <Card
              bordered={false}
              style={{
                borderRadius: 16,

                borderLeft:
                  `5px solid ${BLACK}`,

                boxShadow:
                  "0 8px 25px rgba(0,0,0,0.08)",
              }}
            >
              <Statistic
                title="Total Yearly Fees"
                value={
                  totalFees
                }
                precision={2}
                prefix="$"
              />
            </Card>
          </Col>

          <Col
            xs={24}
            sm={12}
            lg={8}
          >
            <Card
              bordered={false}
              style={{
                borderRadius: 16,

                background:
                  "linear-gradient(135deg, #ff7900, #111111)",

                color:
                  "#ffffff",

                boxShadow:
                  "0 8px 25px rgba(0,0,0,0.15)",
              }}
            >
              <Statistic
                title={
                  <span
                    style={{
                      color:
                        "#ffffff",
                    }}
                  >
                    Current Academic Year
                  </span>
                }
                value={
                  selectedYear
                    ?.name ||
                  "Not Selected"
                }
                valueStyle={{
                  color:
                    "#ffffff",

                  fontSize: 24,
                }}
              />
            </Card>
          </Col>
        </Row>

        {/* =========================================================
            FORM CARD
        ========================================================= */}

        <Card
          bordered={false}
          title={
            <Space>
              <DollarOutlined
                style={{
                  color:
                    ORANGE,
                }}
              />

              <span>
                {editData
                  ? "Edit Fee Structure"
                  : "Create Fee Structure"}
              </span>
            </Space>
          }
          extra={
            editData && (
              <Button
                onClick={() => {
                  setEditData(
                    null
                  );

                  form.resetFields();
                }}
              >
                Cancel Edit
              </Button>
            )
          }
          style={{
            borderRadius: 18,

            marginBottom: 24,

            boxShadow:
              "0 8px 25px rgba(0,0,0,0.08)",
          }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={
              handleSubmit
            }
          >
            <Row
              gutter={[
                16,
                0,
              ]}
            >
              {/* GRADE CLASS */}

              <Col
                xs={24}
                sm={12}
                lg={6}
              >
                <Form.Item
                  label="Grade Class"
                  name="grade_class"
                  rules={[
                    {
                      required:
                        true,

                      message:
                        "Select grade class",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    placeholder="Select grade class"
                    optionFilterProp="children"
                    size="large"
                  >
                    {gradeClasses.map(
                      (
                        grade
                      ) => (
                        <Option
                          key={
                            grade.id
                          }
                          value={
                            grade.id
                          }
                        >
                          {
                            grade.name
                          }
                        </Option>
                      )
                    )}
                  </Select>
                </Form.Item>
              </Col>

              {/* STUDENT TYPE */}

              <Col
                xs={24}
                sm={12}
                lg={6}
              >
                <Form.Item
                  label="Student Type"
                  name="student_type"
                  rules={[
                    {
                      required:
                        true,

                      message:
                        "Select student type",
                    },
                  ]}
                >
                  <StudentTypeSelect />
                </Form.Item>
              </Col>

              {/* YEARLY FEE */}

              <Col
                xs={24}
                sm={12}
                lg={6}
              >
                <Form.Item
                  label="Yearly School Fee"
                  name="yearly_school_fee"
                  rules={[
                    {
                      required:
                        true,

                      message:
                        "Enter yearly fee",
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    prefix="$"
                    size="large"
                    style={{
                      width:
                        "100%",
                    }}
                  />
                </Form.Item>
              </Col>

              {/* REGISTRATION */}

              <Col
                xs={24}
                sm={12}
                lg={6}
              >
                <Form.Item
                  label="Registration"
                  name="registration"
                  rules={[
                    {
                      required:
                        true,

                      message:
                        "Enter registration fee",
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    prefix="$"
                    size="large"
                    style={{
                      width:
                        "100%",
                    }}
                    onChange={(
                      value
                    ) =>
                      handleInstallmentChange(
                        "registration",
                        value
                      )
                    }
                  />
                </Form.Item>
              </Col>

              {/* FIRST */}

              <Col
                xs={24}
                sm={12}
                lg={6}
              >
                <Form.Item
                  label="1st Installment"
                  name="first_installment"
                  rules={[
                    {
                      required:
                        true,
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    prefix="$"
                    size="large"
                    style={{
                      width:
                        "100%",
                    }}
                    onChange={(
                      value
                    ) =>
                      handleInstallmentChange(
                        "first_installment",
                        value
                      )
                    }
                  />
                </Form.Item>
              </Col>

              {/* SECOND */}

              <Col
                xs={24}
                sm={12}
                lg={6}
              >
                <Form.Item
                  label="2nd Installment"
                  name="second_installment"
                  rules={[
                    {
                      required:
                        true,
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    prefix="$"
                    size="large"
                    style={{
                      width:
                        "100%",
                    }}
                    onChange={(
                      value
                    ) =>
                      handleInstallmentChange(
                        "second_installment",
                        value
                      )
                    }
                  />
                </Form.Item>
              </Col>

              {/* THIRD */}

              <Col
                xs={24}
                sm={12}
                lg={6}
              >
                <Form.Item
                  label="3rd Installment"
                  name="third_installment"
                  rules={[
                    {
                      required:
                        true,
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    prefix="$"
                    size="large"
                    style={{
                      width:
                        "100%",
                    }}
                    onChange={(
                      value
                    ) =>
                      handleInstallmentChange(
                        "third_installment",
                        value
                      )
                    }
                  />
                </Form.Item>
              </Col>

              {/* FOURTH */}

              <Col
                xs={24}
                sm={12}
                lg={6}
              >
                <Form.Item
                  label="4th Installment (Auto)"
                  name="fourth_installment"
                >
                  <InputNumber
                    readOnly
                    prefix="$"
                    size="large"
                    style={{
                      width:
                        "100%",

                      background:
                        "#f1f1f1",
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Button
              htmlType="submit"
              type="primary"
              size="large"
              loading={
                loading
              }
              icon={
                editData ? (
                  <EditOutlined />
                ) : (
                  <PlusOutlined />
                )
              }
              style={{
                background:
                  ORANGE,

                borderColor:
                  ORANGE,

                minWidth: 180,

                fontWeight: 700,
              }}
            >
              {editData
                ? "Update Fee Structure"
                : "Create Fee Structure"}
            </Button>
          </Form>
        </Card>

        {/* =========================================================
            SEARCH
        ========================================================= */}

        <Card
          bordered={false}
          style={{
            borderRadius: 18,

            marginBottom: 24,

            boxShadow:
              "0 8px 25px rgba(0,0,0,0.08)",
          }}
        >
          <Row
            justify="space-between"
            align="middle"
            gutter={[
              16,
              16,
            ]}
          >
            <Col
              xs={24}
              md={16}
            >
              <Input
                size="large"
                allowClear
                prefix={
                  <SearchOutlined
                    style={{
                      color:
                        ORANGE,
                    }}
                  />
                }
                placeholder="Search by grade class or student type..."
                value={
                  searchText
                }
                onChange={(e) =>
                  setSearchText(
                    e.target
                      .value
                  )
                }
              />
            </Col>

            <Col
              xs={24}
              md={8}
            >
              <Button
                block
                size="large"
                icon={
                  <PlusOutlined />
                }
                onClick={
                  openCreateModal
                }
                style={{
                  background:
                    BLACK,

                  borderColor:
                    BLACK,

                  color:
                    "#ffffff",

                  fontWeight: 700,
                }}
              >
                New Fee Structure
              </Button>
            </Col>
          </Row>
        </Card>

        {/* =========================================================
            CHART
        ========================================================= */}

        <Card
          bordered={false}
          title={
            <Space>
              <BarChartOutlined
                style={{
                  color:
                    ORANGE,
                }}
              />

              <span>
                Yearly Fee Analysis
              </span>
            </Space>
          }
          style={{
            borderRadius: 18,

            marginBottom: 24,

            boxShadow:
              "0 8px 25px rgba(0,0,0,0.08)",
          }}
        >
          <div
            style={{
              width:
                "100%",

              height: 340,
            }}
          >
            <ResponsiveContainer>
              <BarChart
                data={
                  chartData
                }
                margin={{
                  top: 20,
                  right: 20,
                  left: 10,
                  bottom: 50,
                }}
              >
                <XAxis
                  dataKey="name"
                  angle={-30}
                  textAnchor="end"
                  interval={0}
                />

                <YAxis />

                <RechartsTooltip />

                <Legend />

                <Bar
                  dataKey="fee"
                  name="Yearly Fee"
                  radius={[
                    8,
                    8,
                    0,
                    0,
                  ]}
                >
                  {chartData.map(
                    (
                      _,
                      index
                    ) => (
                      <Cell
                        key={
                          index
                        }
                        fill={
                          index %
                            2 ===
                          0
                            ? ORANGE
                            : BLACK
                        }
                      />
                    )
                  )}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* =========================================================
            TABLE
        ========================================================= */}

        <Card
          bordered={false}
          title={
            <Space>
              <DollarOutlined
                style={{
                  color:
                    ORANGE,
                }}
              />

              <span>
                School Fees Records
              </span>

              <Tag color="orange">
                {
                  filteredData.length
                }
              </Tag>
            </Space>
          }
          style={{
            borderRadius: 18,

            boxShadow:
              "0 8px 25px rgba(0,0,0,0.08)",
          }}
        >
          <Table
            loading={
              loading
            }
            columns={
              columns
            }
            dataSource={
              filteredData
            }
            rowKey="id"
            pagination={{
              current:
                page,

              pageSize:
                rowsPerPage,

              total:
                filteredData.length,

              onChange:
                (
                  newPage
                ) =>
                  setPage(
                    newPage
                  ),

              showSizeChanger:
                false,

              showTotal:
                (
                  total
                ) =>
                  `Total ${total} records`,
            }}
            scroll={{
              x: 1400,
            }}
            size="middle"
            bordered
            rowClassName={() =>
              "professional-fee-row"
            }
          />
        </Card>
      </div>

      {/* ===========================================================
          MOBILE / CREATE / EDIT MODAL
      =========================================================== */}

      <Modal
        open={
          modalOpen
        }
        title={
          editData
            ? "Edit Fee Structure"
            : "Create Fee Structure"
        }
        footer={null}
        onCancel={
          closeModal
        }
        width="min(95vw, 900px)"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={
            handleSubmit
          }
        >
          <Row
            gutter={[
              16,
              0,
            ]}
          >
            {/* GRADE CLASS */}

            <Col
              xs={24}
              sm={12}
            >
              <Form.Item
                label="Grade Class"
                name="grade_class"
                rules={[
                  {
                    required:
                      true,

                    message:
                      "Select grade class",
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select grade class"
                  optionFilterProp="children"
                  size="large"
                >
                  {gradeClasses.map(
                    (
                      grade
                    ) => (
                      <Option
                        key={
                          grade.id
                        }
                        value={
                          grade.id
                        }
                      >
                        {
                          grade.name
                        }
                      </Option>
                    )
                  )}
                </Select>
              </Form.Item>
            </Col>

            {/* STUDENT TYPE */}

            <Col
              xs={24}
              sm={12}
            >
              <Form.Item
                label="Student Type"
                name="student_type"
                rules={[
                  {
                    required:
                      true,

                    message:
                      "Select student type",
                  },
                ]}
              >
                <StudentTypeSelect />
              </Form.Item>
            </Col>

            {/* YEARLY FEE */}

            <Col
              xs={24}
              sm={12}
            >
              <Form.Item
                label="Yearly School Fee"
                name="yearly_school_fee"
                rules={[
                  {
                    required:
                      true,

                    message:
                      "Enter yearly fee",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  precision={2}
                  prefix="$"
                  size="large"
                  style={{
                    width:
                      "100%",
                  }}
                />
              </Form.Item>
            </Col>

            {/* REGISTRATION */}

            <Col
              xs={24}
              sm={12}
            >
              <Form.Item
                label="Registration"
                name="registration"
                rules={[
                  {
                    required:
                      true,

                    message:
                      "Enter registration fee",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  precision={2}
                  prefix="$"
                  size="large"
                  style={{
                    width:
                      "100%",
                  }}
                  onChange={(
                    value
                  ) =>
                    handleInstallmentChange(
                      "registration",
                      value
                    )
                  }
                />
              </Form.Item>
            </Col>

            {/* INSTALLMENTS */}

            {[
              [
                "first_installment",
                "1st Installment",
              ],

              [
                "second_installment",
                "2nd Installment",
              ],

              [
                "third_installment",
                "3rd Installment",
              ],
            ].map(
              ([
                name,
                label,
              ]) => (
                <Col
                  xs={24}
                  sm={12}
                  key={
                    name
                  }
                >
                  <Form.Item
                    label={
                      label
                    }
                    name={
                      name
                    }
                    rules={[
                      {
                        required:
                          true,
                      },
                    ]}
                  >
                    <InputNumber
                      min={
                        0
                      }
                      precision={
                        2
                      }
                      prefix="$"
                      size="large"
                      style={{
                        width:
                          "100%",
                      }}
                      onChange={(
                        value
                      ) =>
                        handleInstallmentChange(
                          name,
                          value
                        )
                      }
                    />
                  </Form.Item>
                </Col>
              )
            )}

            {/* FOURTH */}

            <Col
              xs={24}
              sm={12}
            >
              <Form.Item
                label="4th Installment (Auto)"
                name="fourth_installment"
              >
                <InputNumber
                  readOnly
                  prefix="$"
                  size="large"
                  style={{
                    width:
                      "100%",
                    background:
                      "#f1f1f1",
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Button
            htmlType="submit"
            type="primary"
            block
            size="large"
            loading={
              loading
            }
            icon={
              editData ? (
                <EditOutlined />
              ) : (
                <PlusOutlined />
              )
            }
            style={{
              background:
                ORANGE,

              borderColor:
                ORANGE,

              fontWeight: 700,
            }}
          >
            {editData
              ? "Update Fee Structure"
              : "Create Fee Structure"}
          </Button>
        </Form>
      </Modal>

      {/* ===========================================================
          STYLES
      =========================================================== */}

      <style>
        {`
          .professional-fee-row:hover td {
            background: #fff3e8 !important;
          }

          .ant-table-thead > tr > th {
            background: #111111 !important;
            color: #ffffff !important;
            font-weight: 700;
            white-space: nowrap;
          }

          .ant-table-tbody > tr > td {
            white-space: nowrap;
            font-size: 14px;
          }

          .ant-card-head-title {
            font-weight: 800;
          }

          .ant-select-selector {
            font-weight: 500;
          }

          @media (max-width: 768px) {
            .ant-card {
              border-radius: 14px !important;
            }

            .ant-table {
              font-size: 13px;
            }

            .ant-statistic-content {
              font-size: 22px !important;
            }
          }
        `}
      </style>
    </Layout>
  );
};

export default SchoolFeesData;





// import React, { useEffect, useState } from "react";
// import { pdf } from "@react-pdf/renderer";
// import {
//   AutoComplete,
//   Button,
//   Card,
//   Col,
//   Form,
//   Input,
//   InputNumber,
//   Layout,
//   Modal,
//   Pagination,
//   Row,
//   Select,
//   Space,
//   Statistic,
//   Table,
//   Tag,
//   Tooltip,
//   Typography,
//   message,
//   Popconfirm,
// } from "antd";

// import {
//   SearchOutlined,
//   EditOutlined,
//   DeleteOutlined,
//   PlusOutlined,
//   DollarOutlined,
//   BarChartOutlined,
//   ReloadOutlined,
// } from "@ant-design/icons";

// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   Tooltip as RechartsTooltip,
//   ResponsiveContainer,
//   Legend,
//   Cell,
// } from "recharts";

// import Swal from "sweetalert2";
// import api from "../api/api";
// import { useAcademicYear } from "../AcademicYearContext";
// import InformationSheetPDF from "./InformationSheetPDF";

// const { Title, Text } = Typography;
// const { Option } = Select;

// const ORANGE = "#ff7900";
// const BLACK = "#111111";

// const SchoolFeesData = () => {
//   const [form] = Form.useForm();

//   const [gradeClasses, setGradeClasses] = useState([]);
//   const [feesData, setFeesData] = useState([]);
//   const [filteredData, setFilteredData] = useState([]);

//   const [loading, setLoading] = useState(false);
//   const [searchText, setSearchText] = useState("");

//   const [page, setPage] = useState(1);
//   const [rowsPerPage] = useState(4);

//   const [editData, setEditData] = useState(null);
//   const [modalOpen, setModalOpen] = useState(false);

//   const { selectedYear } = useAcademicYear();

//   /*
//   |--------------------------------------------------------------------------
//   | LOAD DATA
//   |--------------------------------------------------------------------------
//   */

//   useEffect(() => {
//     if (!selectedYear) return;

//     const loadData = async () => {
//       try {
//         setLoading(true);

//         const classRes = await api.get(
//           "/api/users/grade-classes/"
//         );

//         const classes =
//           classRes.data.results || classRes.data;

//         setGradeClasses(classes);

//         const feesRes = await api.get(
//           "/api/users/school-fees-data/",
//           {
//             params: {
//               academic_year: selectedYear.id,
//             },
//           }
//         );

//         const results =
//           feesRes.data.results || feesRes.data;

//         const mapped = results.map((item) => {
//           const matchedClass = classes.find(
//             (cls) => cls.id === item.grade_class
//           );

//           return {
//             ...item,
//             grade_class:
//               matchedClass || item.grade_class,
//           };
//         });

//         setFeesData(mapped);
//         setFilteredData(mapped);
//       } catch (err) {
//         console.error("Error loading data", err);

//         Swal.fire(
//           "Error",
//           "Failed to load initial data",
//           "error"
//         );
//       } finally {
//         setLoading(false);
//       }
//     };

//     loadData();
//   }, [selectedYear]);

//   /*
//   |--------------------------------------------------------------------------
//   | SEARCH
//   |--------------------------------------------------------------------------
//   */

//   useEffect(() => {
//     const filtered = feesData.filter((item) =>
//       item.grade_class?.name
//         ?.toLowerCase()
//         .includes(searchText.toLowerCase())
//     );

//     setFilteredData(filtered);
//     setPage(1);
//   }, [feesData, searchText]);

//   /*
//   |--------------------------------------------------------------------------
//   | INSTALLMENT CALCULATION
//   |--------------------------------------------------------------------------
//   */

//   const downloadInformationSheet = async () => {

//   try {

//     const blob = await pdf(
//       <InformationSheetPDF
//         fees={filteredData}
//         academicYear={selectedYear}
//       />
//     ).toBlob();


//     const url = URL.createObjectURL(blob);


//     const link = document.createElement("a");

//     link.href = url;

//     link.download =
//       "Class_Fees_Information_Sheet.pdf";


//     link.click();


//     URL.revokeObjectURL(url);


//   } catch(error){

//     console.error(error);

//     Swal.fire(
//       "Error",
//       "Unable to generate information sheet",
//       "error"
//     );

//   }

// };

//   const handleInstallmentChange = (
//     field,
//     value
//   ) => {
//     const values = form.getFieldsValue();

//     const yearly =
//       Number(values.yearly_school_fee) || 0;

//     const registration =
//       Number(
//         field === "registration"
//           ? value
//           : values.registration
//       ) || 0;

//     const first =
//       Number(
//         field === "first_installment"
//           ? value
//           : values.first_installment
//       ) || 0;

//     const second =
//       Number(
//         field === "second_installment"
//           ? value
//           : values.second_installment
//       ) || 0;

//     const third =
//       Number(
//         field === "third_installment"
//           ? value
//           : values.third_installment
//       ) || 0;

//     const remaining =
//       yearly -
//       registration -
//       first -
//       second -
//       third;

//     form.setFieldsValue({
//       [field]: value,
//       fourth_installment:
//         remaining >= 0
//           ? remaining.toFixed(2)
//           : "0.00",
//     });
//   };

//   /*
//   |--------------------------------------------------------------------------
//   | OPEN CREATE MODAL
//   |--------------------------------------------------------------------------
//   */

//   const openCreateModal = () => {
//     setEditData(null);

//     form.resetFields();

//     setModalOpen(true);
//   };

//   /*
//   |--------------------------------------------------------------------------
//   | EDIT
//   |--------------------------------------------------------------------------
//   */

//   const handleEdit = (data) => {
//     setEditData(data);

//     form.setFieldsValue({
//       grade_class: data.grade_class?.id,
//       student_type: data.student_type,
//       yearly_school_fee:
//         data.yearly_school_fee,
//       registration:
//         data.registration,
//       first_installment:
//         data.first_installment,
//       second_installment:
//         data.second_installment,
//       third_installment:
//         data.third_installment,
//       fourth_installment:
//         data.fourth_installment,
//     });

//     setModalOpen(true);
//   };

//   /*
//   |--------------------------------------------------------------------------
//   | SUBMIT
//   |--------------------------------------------------------------------------
//   */

//   const handleSubmit = async (values) => {
//     if (!selectedYear) {
//       Swal.fire(
//         "Select Academic Year",
//         "Please select an academic year first",
//         "warning"
//       );

//       return;
//     }

//     setLoading(true);

//     const yearly =
//       Number(values.yearly_school_fee) || 0;

//     const totalInstallments =
//       Number(values.registration || 0) +
//       Number(values.first_installment || 0) +
//       Number(values.second_installment || 0) +
//       Number(values.third_installment || 0) +
//       Number(values.fourth_installment || 0);

//     if (
//       Math.abs(
//         totalInstallments - yearly
//       ) > 0.01
//     ) {
//       Swal.fire(
//         "Validation Error",
//         `Total installments (${totalInstallments.toFixed(
//           2
//         )}) must equal yearly fee (${yearly.toFixed(
//           2
//         )})`,
//         "error"
//       );

//       setLoading(false);

//       return;
//     }

//     const payload = {
//       academic_year: selectedYear.id,
//       grade_class: values.grade_class,
//       student_type: values.student_type,
//       yearly_school_fee:
//         values.yearly_school_fee,
//       registration:
//         values.registration,
//       first_installment:
//         values.first_installment,
//       second_installment:
//         values.second_installment,
//       third_installment:
//         values.third_installment,
//       fourth_installment:
//         values.fourth_installment,
//     };

//     try {
//       let updatedData;

//       if (editData) {
//         const res = await api.put(
//           `/api/users/school-fees-data/${editData.id}/`,
//           payload
//         );

//         const selectedClass =
//           gradeClasses.find(
//             (cls) =>
//               cls.id === values.grade_class
//           );

//         updatedData = {
//           ...res.data,
//           grade_class: selectedClass,
//         };

//         const updatedFees =
//           feesData.map((item) =>
//             item.id === editData.id
//               ? updatedData
//               : item
//           );

//         setFeesData(updatedFees);

//         Swal.fire(
//           "Updated!",
//           "Record updated successfully.",
//           "success"
//         );
//       } else {
//         const exists = feesData.some(
//           (item) =>
//             item.grade_class?.id ===
//               payload.grade_class &&
//             item.student_type ===
//               payload.student_type
//         );

//         if (exists) {
//           Swal.fire(
//             "Duplicate Fee",
//             "This grade class and student type already has a fee for this academic year.",
//             "warning"
//           );

//           setLoading(false);

//           return;
//         }

//         const res = await api.post(
//           "/api/users/school-fees-data/",
//           payload
//         );

//         const selectedClass =
//           gradeClasses.find(
//             (cls) =>
//               cls.id === values.grade_class
//           );

//         updatedData = {
//           ...res.data,
//           grade_class: selectedClass,
//         };

//         setFeesData([
//           updatedData,
//           ...feesData,
//         ]);

//         Swal.fire(
//           "Created!",
//           "Record created successfully.",
//           "success"
//         );
//       }

//       setModalOpen(false);

//       form.resetFields();

//       setEditData(null);
//     } catch (error) {
//       console.error(
//         "Error saving data:",
//         error.response?.data || error
//       );

//       Swal.fire(
//         "Error",
//         "Failed to save data.",
//         "error"
//       );
//     } finally {
//       setLoading(false);
//     }
//   };

//   /*
//   |--------------------------------------------------------------------------
//   | DELETE
//   |--------------------------------------------------------------------------
//   */

//   const handleDelete = async (id) => {
//     try {
//       await api.delete(
//         `/api/users/school-fees-data/${id}/`
//       );

//       const updatedFees =
//         feesData.filter(
//           (item) => item.id !== id
//         );

//       setFeesData(updatedFees);

//       Swal.fire(
//         "Deleted!",
//         "The record has been deleted.",
//         "success"
//       );
//     } catch (error) {
//       console.error(
//         "Error deleting data",
//         error
//       );

//       Swal.fire(
//         "Error",
//         "Failed to delete record.",
//         "error"
//       );
//     }
//   };

//   /*
//   |--------------------------------------------------------------------------
//   | HELPERS
//   |--------------------------------------------------------------------------
//   */

//   const money = (value) =>
//     `$${Number(value || 0).toFixed(2)}`;

//   const totalRecords = filteredData.length;

//   const totalFees = filteredData.reduce(
//     (sum, item) =>
//       sum +
//       Number(
//         item.yearly_school_fee || 0
//       ),
//     0
//   );

//   /*
//   |--------------------------------------------------------------------------
//   | TABLE COLUMNS
//   |--------------------------------------------------------------------------
//   */

//   const columns = [
//     {
//       title: "GRADE CLASS",
//       dataIndex: ["grade_class", "name"],
//       key: "grade_class",
//       fixed: "left",
//       width: 170,
//       render: (text) => (
//         <Text strong>
//           {text || "N/A"}
//         </Text>
//       ),
//     },

//     // {
//     //   title: "STUDENT TYPE",
//     //   dataIndex: "student_type",
//     //   key: "student_type",
//     //   width: 150,
//     //   render: (type) => (
//     //     <Tag
//     //       color={
//     //         type === "new"
//     //           ? "orange"
//     //           : "default"
//     //       }
//     //       style={{
//     //         fontWeight: 600,
//     //         textTransform: "uppercase",
//     //       }}
//     //     >
//     //       {type} Student
//     //     </Tag>
//     //   ),
//     // },

//     {
//   title: "STUDENT TYPE",
//   dataIndex: "student_type",
//   key: "student_type",
//   width: 150,
//   render: (type) => (
//     <Tag
//       style={{
//         fontWeight: 700,
//         textTransform: "uppercase",
//         fontSize:
//           type === "scholarship"
//             ? "11px"
//             : "12px",
//         color:
//           type === "scholarship"
//             ? "#ffffff"
//             : "#111111",
//         background:
//           type === "scholarship"
//             ? "#001f5b"
//             : type === "new"
//             ? "#ff7900"
//             : "#e5e7eb",
//         borderColor:
//           type === "scholarship"
//             ? "#001f5b"
//             : "transparent",
//         padding:
//           type === "scholarship"
//             ? "2px 6px"
//             : "4px 10px",
//         borderRadius: 6,
//         whiteSpace: "nowrap",
//       }}
//     >
//       {type === "scholarship"
//         ? "SCHOLARSHIP"
//         : `${type} Student`}
//     </Tag>
//   ),
// },

//     {
//       title: "YEARLY FEE",
//       dataIndex: "yearly_school_fee",
//       key: "yearly_school_fee",
//       width: 140,
//       render: money,
//     },

//     {
//       title: "REGISTRATION",
//       dataIndex: "registration",
//       key: "registration",
//       width: 150,
//       render: money,
//     },

//     {
//       title: "1ST INSTALLMENT",
//       dataIndex: "first_installment",
//       key: "first_installment",
//       width: 150,
//       render: money,
//     },

//     {
//       title: "2ND INSTALLMENT",
//       dataIndex: "second_installment",
//       key: "second_installment",
//       width: 150,
//       render: money,
//     },

//     {
//       title: "3RD INSTALLMENT",
//       dataIndex: "third_installment",
//       key: "third_installment",
//       width: 150,
//       render: money,
//     },

//     {
//       title: "4TH INSTALLMENT",
//       dataIndex: "fourth_installment",
//       key: "fourth_installment",
//       width: 150,
//       render: money,
//     },

//     {
//       title: "ACTIONS",
//       key: "actions",
//       fixed: "right",
//       width: 150,
//       render: (_, record) => (
//         <Space>
//           <Tooltip title="Edit Record">
//             <Button
//               type="primary"
//               icon={<EditOutlined />}
//               onClick={() =>
//                 handleEdit(record)
//               }
//               style={{
//                 background: BLACK,
//                 borderColor: BLACK,
//               }}
//             />
//           </Tooltip>

//           <Popconfirm
//             title="Delete this fee record?"
//             description="This action cannot be undone."
//             onConfirm={() =>
//               handleDelete(record.id)
//             }
//             okText="Delete"
//             cancelText="Cancel"
//           >
//             <Tooltip title="Delete Record">
//               <Button
//                 danger
//                 icon={<DeleteOutlined />}
//               />
//             </Tooltip>
//           </Popconfirm>
//         </Space>
//       ),
//     },
//   ];

//   /*
//   |--------------------------------------------------------------------------
//   | CHART DATA
//   |--------------------------------------------------------------------------
//   */

//   const chartData = filteredData.map(
//     (item) => ({
//       name:
//         item.grade_class?.name ||
//         "Unknown",
//       fee: Number(
//         item.yearly_school_fee || 0
//       ),
//     })
//   );

//   /*
//   |--------------------------------------------------------------------------
//   | UI
//   |--------------------------------------------------------------------------
//   */

//   return (
//     <Layout
//       style={{
//         minHeight: "100vh",
//         background:
//           "linear-gradient(135deg, #f5f5f5 0%, #ffffff 50%, #fff3e8 100%)",
//       }}
//     >
//       <div
//         style={{
//           padding: "clamp(16px, 3vw, 32px)",
//           maxWidth: 1700,
//           margin: "0 auto",
//           width: "100%",
//         }}
//       >
//         {/* HEADER */}

//         <Card
//           bordered={false}
//           style={{
//             marginBottom: 24,
//             borderRadius: 20,
//             overflow: "hidden",
//             background:
//               "linear-gradient(135deg, #111111 0%, #242424 60%, #ff7900 160%)",
//             boxShadow:
//               "0 15px 40px rgba(0,0,0,0.16)",
//           }}
//         >
//           <Row
//             align="middle"
//             justify="space-between"
//             gutter={[20, 20]}
//           >
//             <Col xs={24} md={16}>
//               <Space
//                 direction="vertical"
//                 size={4}
//               >
//                 <Text
//                   style={{
//                     color: ORANGE,
//                     fontWeight: 800,
//                     letterSpacing: 2,
//                   }}
//                 >
//                   FINANCE MANAGEMENT
//                 </Text>

//                 <Title
//                   level={2}
//                   style={{
//                     color: "#ffffff",
//                     margin: 0,
//                     fontSize:
//                       "clamp(24px, 4vw, 38px)",
//                   }}
//                 >
//                   School Fees Management
//                 </Title>

//                 <Text
//                   style={{
//                     color: "#d9d9d9",
//                     fontSize: 15,
//                   }}
//                 >
//                   Configure and manage student
//                   fee structures by academic year.
//                 </Text>
//               </Space>
//             </Col>

//             <Col xs={24} md={8}>
//               <div
//                 style={{
//                   textAlign:
//                     window.innerWidth < 768
//                       ? "left"
//                       : "right",
//                 }}
//               >
//                 <Text
//                   style={{
//                     color: "#bbbbbb",
//                     display: "block",
//                   }}
//                 >
//                   ACTIVE ACADEMIC YEAR
//                 </Text>

//                 <Title
//                   level={3}
//                   style={{
//                     color: ORANGE,
//                     margin: "4px 0 0",
//                   }}
//                 >
//                   {selectedYear?.name ||
//                     "None Selected"}
//                 </Title>
//               </div>
//             </Col>
//           </Row>
//         </Card>

//         {/* STATISTICS */}

//         <Row
//           gutter={[16, 16]}
//           style={{
//             marginBottom: 24,
//           }}
//         >
//           <Col xs={24} sm={12} lg={8}>
//             <Card
//               bordered={false}
//               style={{
//                 borderRadius: 16,
//                 borderLeft:
//                   `5px solid ${ORANGE}`,
//                 boxShadow:
//                   "0 8px 25px rgba(0,0,0,0.08)",
//               }}
//             >
//               <Statistic
//                 title="Fee Records"
//                 value={totalRecords}
//                 prefix={
//                   <BarChartOutlined
//                     style={{
//                       color: ORANGE,
//                     }}
//                   />
//                 }
//               />
//             </Card>
//           </Col>

//           <Col xs={24} sm={12} lg={8}>
//             <Card
//               bordered={false}
//               style={{
//                 borderRadius: 16,
//                 borderLeft:
//                   `5px solid ${BLACK}`,
//                 boxShadow:
//                   "0 8px 25px rgba(0,0,0,0.08)",
//               }}
//             >
//               <Statistic
//                 title="Total Yearly Fees"
//                 value={totalFees}
//                 precision={2}
//                 prefix="$"
//               />
//             </Card>
//           </Col>

//           <Col xs={24} sm={12} lg={8}>
//             <Card
//               bordered={false}
//               style={{
//                 borderRadius: 16,
//                 background:
//                   "linear-gradient(135deg, #ff7900, #111111)",
//                 color: "#ffffff",
//                 boxShadow:
//                   "0 8px 25px rgba(0,0,0,0.15)",
//               }}
//             >
//               <Statistic
//                 title={
//                   <span
//                     style={{
//                       color: "#ffffff",
//                     }}
//                   >
//                     Current Academic Year
//                   </span>
//                 }
//                 value={
//                   selectedYear?.name ||
//                   "Not Selected"
//                 }
//                 valueStyle={{
//                   color: "#ffffff",
//                   fontSize: 24,
//                 }}
//               />
//             </Card>
//           </Col>
//         </Row>

//         {/* FORM CARD */}

//         <Card
//           bordered={false}
//           title={
//             <Space>
//               <DollarOutlined
//                 style={{
//                   color: ORANGE,
//                 }}
//               />

//               <span>
//                 {editData
//                   ? "Edit Fee Structure"
//                   : "Create Fee Structure"}
//               </span>
//             </Space>
//           }
//           extra={
//             editData && (
//               <Button
//                 onClick={() => {
//                   setEditData(null);
//                   form.resetFields();
//                 }}
//               >
//                 Cancel Edit
//               </Button>
//             )
//           }
//           style={{
//             borderRadius: 18,
//             marginBottom: 24,
//             boxShadow:
//               "0 8px 25px rgba(0,0,0,0.08)",
//           }}
//         >
//           <Form
//             form={form}
//             layout="vertical"
//             onFinish={handleSubmit}
//           >
//             <Row gutter={[16, 0]}>
//               <Col xs={24} sm={12} lg={6}>
//                 <Form.Item
//                   label="Grade Class"
//                   name="grade_class"
//                   rules={[
//                     {
//                       required: true,
//                       message:
//                         "Select grade class",
//                     },
//                   ]}
//                 >
//                   <Select
//                     showSearch
//                     placeholder="Select grade class"
//                     optionFilterProp="children"
//                     size="large"
//                   >
//                     {gradeClasses.map(
//                       (grade) => (
//                         <Option
//                           key={grade.id}
//                           value={grade.id}
//                         >
//                           {grade.name}
//                         </Option>
//                       )
//                     )}
//                   </Select>
//                 </Form.Item>
//               </Col>

//               <Col xs={24} sm={12} lg={6}>
//                 <Form.Item
//                   label="Student Type"
//                   name="student_type"
//                   rules={[
//                     {
//                       required: true,
//                       message:
//                         "Select student type",
//                     },
//                   ]}
//                 >
//                   <Select
//                     placeholder="Select type"
//                     size="large"
//                   >
//                     <Option value="new">
//                       New Student
//                     </Option>

//                     <Option value="old">
//                       Old Student
//                     </Option>

//                     <Option value="scholarship">
//                       Scholarship Student
//                     </Option>
//                   </Select>
//                 </Form.Item>
//               </Col>

//               <Col xs={24} sm={12} lg={6}>
//                 <Form.Item
//                   label="Yearly School Fee"
//                   name="yearly_school_fee"
//                   rules={[
//                     {
//                       required: true,
//                       message:
//                         "Enter yearly fee",
//                     },
//                   ]}
//                 >
//                   <InputNumber
//                     min={0}
//                     precision={2}
//                     prefix="$"
//                     size="large"
//                     style={{
//                       width: "100%",
//                     }}
//                   />
//                 </Form.Item>
//               </Col>

//               <Col xs={24} sm={12} lg={6}>
//                 <Form.Item
//                   label="Registration"
//                   name="registration"
//                   rules={[
//                     {
//                       required: true,
//                       message:
//                         "Enter registration fee",
//                     },
//                   ]}
//                 >
//                   <InputNumber
//                     min={0}
//                     precision={2}
//                     prefix="$"
//                     size="large"
//                     style={{
//                       width: "100%",
//                     }}
//                     onChange={(value) =>
//                       handleInstallmentChange(
//                         "registration",
//                         value
//                       )
//                     }
//                   />
//                 </Form.Item>
//               </Col>

//               <Col xs={24} sm={12} lg={6}>
//                 <Form.Item
//                   label="1st Installment"
//                   name="first_installment"
//                   rules={[
//                     {
//                       required: true,
//                     },
//                   ]}
//                 >
//                   <InputNumber
//                     min={0}
//                     precision={2}
//                     prefix="$"
//                     size="large"
//                     style={{
//                       width: "100%",
//                     }}
//                     onChange={(value) =>
//                       handleInstallmentChange(
//                         "first_installment",
//                         value
//                       )
//                     }
//                   />
//                 </Form.Item>
//               </Col>

//               <Col xs={24} sm={12} lg={6}>
//                 <Form.Item
//                   label="2nd Installment"
//                   name="second_installment"
//                   rules={[
//                     {
//                       required: true,
//                     },
//                   ]}
//                 >
//                   <InputNumber
//                     min={0}
//                     precision={2}
//                     prefix="$"
//                     size="large"
//                     style={{
//                       width: "100%",
//                     }}
//                     onChange={(value) =>
//                       handleInstallmentChange(
//                         "second_installment",
//                         value
//                       )
//                     }
//                   />
//                 </Form.Item>
//               </Col>

//               <Col xs={24} sm={12} lg={6}>
//                 <Form.Item
//                   label="3rd Installment"
//                   name="third_installment"
//                   rules={[
//                     {
//                       required: true,
//                     },
//                   ]}
//                 >
//                   <InputNumber
//                     min={0}
//                     precision={2}
//                     prefix="$"
//                     size="large"
//                     style={{
//                       width: "100%",
//                     }}
//                     onChange={(value) =>
//                       handleInstallmentChange(
//                         "third_installment",
//                         value
//                       )
//                     }
//                   />
//                 </Form.Item>
//               </Col>

//               <Col xs={24} sm={12} lg={6}>
//                 <Form.Item
//                   label="4th Installment (Auto)"
//                   name="fourth_installment"
//                 >
//                   <InputNumber
//                     readOnly
//                     prefix="$"
//                     size="large"
//                     style={{
//                       width: "100%",
//                       background:
//                         "#f1f1f1",
//                     }}
//                   />
//                 </Form.Item>
//               </Col>
//             </Row>

//             <Button
//               htmlType="submit"
//               type="primary"
//               size="large"
//               loading={loading}
//               icon={
//                 editData ? (
//                   <EditOutlined />
//                 ) : (
//                   <PlusOutlined />
//                 )
//               }
//               style={{
//                 background: ORANGE,
//                 borderColor: ORANGE,
//                 minWidth: 180,
//                 fontWeight: 700,
//               }}
//             >
//               {editData
//                 ? "Update Fee Structure"
//                 : "Create Fee Structure"}
//             </Button>
//           </Form>
//         </Card>

//         {/* SEARCH */}

//         <Card
//           bordered={false}
//           style={{
//             borderRadius: 18,
//             marginBottom: 24,
//             boxShadow:
//               "0 8px 25px rgba(0,0,0,0.08)",
//           }}
//         >
//           <Row
//             justify="space-between"
//             align="middle"
//             gutter={[16, 16]}
//           >
//             <Col xs={24} md={16}>
//               <Input
//                 size="large"
//                 allowClear
//                 prefix={
//                   <SearchOutlined
//                     style={{
//                       color: ORANGE,
//                     }}
//                   />
//                 }
//                 placeholder="Search by grade class..."
//                 value={searchText}
//                 onChange={(e) =>
//                   setSearchText(
//                     e.target.value
//                   )
//                 }
//               />
//             </Col>

//             <Col xs={24} md={8}>
//               <Button
//                 block
//                 size="large"
//                 icon={<PlusOutlined />}
//                 onClick={openCreateModal}
//                 style={{
//                   background: BLACK,
//                   borderColor: BLACK,
//                   color: "#ffffff",
//                   fontWeight: 700,
//                 }}
//               >
//                 New Fee Structure
//               </Button>

//           {/* <Button
// block
// size="large"
// icon={<DollarOutlined />}
// onClick={downloadInformationSheet}
// >
// Download Class Fees Information Sheet
// </Button> */}
//             </Col>
//           </Row>
//         </Card>

//         {/* CHART */}

//         <Card
//           bordered={false}
//           title={
//             <Space>
//               <BarChartOutlined
//                 style={{
//                   color: ORANGE,
//                 }}
//               />

//               <span>
//                 Yearly Fee Analysis
//               </span>
//             </Space>
//           }
//           style={{
//             borderRadius: 18,
//             marginBottom: 24,
//             boxShadow:
//               "0 8px 25px rgba(0,0,0,0.08)",
//           }}
//         >
//           <div
//             style={{
//               width: "100%",
//               height: 340,
//             }}
//           >
//             <ResponsiveContainer>
//               <BarChart
//                 data={chartData}
//                 margin={{
//                   top: 20,
//                   right: 20,
//                   left: 10,
//                   bottom: 50,
//                 }}
//               >
//                 <XAxis
//                   dataKey="name"
//                   angle={-30}
//                   textAnchor="end"
//                   interval={0}
//                 />

//                 <YAxis />

//                 <RechartsTooltip />

//                 <Legend />

//                 <Bar
//                   dataKey="fee"
//                   name="Yearly Fee"
//                   radius={[
//                     8,
//                     8,
//                     0,
//                     0,
//                   ]}
//                 >
//                   {chartData.map(
//                     (_, index) => (
//                       <Cell
//                         key={index}
//                         fill={
//                           index % 2 === 0
//                             ? ORANGE
//                             : BLACK
//                         }
//                       />
//                     )
//                   )}
//                 </Bar>
//               </BarChart>
//             </ResponsiveContainer>
//           </div>
//         </Card>

//         {/* TABLE */}

//         <Card
//           bordered={false}
//           title={
//             <Space>
//               <DollarOutlined
//                 style={{
//                   color: ORANGE,
//                 }}
//               />

//               <span>
//                 School Fees Records
//               </span>

//               <Tag color="orange">
//                 {filteredData.length}
//               </Tag>
//             </Space>
//           }
//           style={{
//             borderRadius: 18,
//             boxShadow:
//               "0 8px 25px rgba(0,0,0,0.08)",
//           }}
//         >
//           <Table
//             loading={loading}
//             columns={columns}
//             dataSource={filteredData}
//             rowKey="id"
//             pagination={{
//               current: page,
//               pageSize: rowsPerPage,
//               total: filteredData.length,
//               onChange: (newPage) =>
//                 setPage(newPage),
//               showSizeChanger: false,
//               showTotal: (total) =>
//                 `Total ${total} records`,
//             }}
//             scroll={{
//               x: 1400,
//             }}
//             size="middle"
//             bordered
//             rowClassName={() =>
//               "professional-fee-row"
//             }
//           />
//         </Card>
//       </div>

//       {/* MOBILE / CREATE MODAL */}

//       <Modal
//         open={modalOpen}
//         title={
//           editData
//             ? "Edit Fee Structure"
//             : "Create Fee Structure"
//         }
//         footer={null}
//         onCancel={() => {
//           setModalOpen(false);
//           setEditData(null);
//           form.resetFields();
//         }}
//         width="min(95vw, 900px)"
//       >
//         <Form
//           form={form}
//           layout="vertical"
//           onFinish={handleSubmit}
//         >
//           <Row gutter={[16, 0]}>
//             <Col xs={24} sm={12}>
//               <Form.Item
//                 label="Grade Class"
//                 name="grade_class"
//                 rules={[
//                   {
//                     required: true,
//                   },
//                 ]}
//               >
//                 <Select
//                   showSearch
//                   placeholder="Select grade class"
//                 >
//                   {gradeClasses.map(
//                     (grade) => (
//                       <Option
//                         key={grade.id}
//                         value={grade.id}
//                       >
//                         {grade.name}
//                       </Option>
//                     )
//                   )}
//                 </Select>
//               </Form.Item>
//             </Col>

//             <Col xs={24} sm={12}>
//               <Form.Item
//                 label="Student Type"
//                 name="student_type"
//                 rules={[
//                   {
//                     required: true,
//                   },
//                 ]}
//               >
//                 <Select>
//                   <Option value="new">
//                     New Student
//                   </Option>

//                   <Option value="old">
//                     Old Student
//                   </Option>
//                 </Select>
//               </Form.Item>
//             </Col>

//             <Col xs={24} sm={12}>
//               <Form.Item
//                 label="Yearly School Fee"
//                 name="yearly_school_fee"
//                 rules={[
//                   {
//                     required: true,
//                   },
//                 ]}
//               >
//                 <InputNumber
//                   prefix="$"
//                   style={{
//                     width: "100%",
//                   }}
//                 />
//               </Form.Item>
//             </Col>

//             <Col xs={24} sm={12}>
//               <Form.Item
//                 label="Registration"
//                 name="registration"
//                 rules={[
//                   {
//                     required: true,
//                   },
//                 ]}
//               >
//                 <InputNumber
//                   prefix="$"
//                   style={{
//                     width: "100%",
//                   }}
//                   onChange={(value) =>
//                     handleInstallmentChange(
//                       "registration",
//                       value
//                     )
//                   }
//                 />
//               </Form.Item>
//             </Col>

//             {[
//               [
//                 "first_installment",
//                 "1st Installment",
//               ],
//               [
//                 "second_installment",
//                 "2nd Installment",
//               ],
//               [
//                 "third_installment",
//                 "3rd Installment",
//               ],
//             ].map(
//               ([name, label]) => (
//                 <Col
//                   xs={24}
//                   sm={12}
//                   key={name}
//                 >
//                   <Form.Item
//                     label={label}
//                     name={name}
//                     rules={[
//                       {
//                         required: true,
//                       },
//                     ]}
//                   >
//                     <InputNumber
//                       prefix="$"
//                       style={{
//                         width: "100%",
//                       }}
//                       onChange={(value) =>
//                         handleInstallmentChange(
//                           name,
//                           value
//                         )
//                       }
//                     />
//                   </Form.Item>
//                 </Col>
//               )
//             )}

//             <Col xs={24} sm={12}>
//               <Form.Item
//                 label="4th Installment (Auto)"
//                 name="fourth_installment"
//               >
//                 <InputNumber
//                   readOnly
//                   prefix="$"
//                   style={{
//                     width: "100%",
//                   }}
//                 />
//               </Form.Item>
//             </Col>
//           </Row>

//           <Button
//             htmlType="submit"
//             type="primary"
//             block
//             loading={loading}
//             style={{
//               background: ORANGE,
//               borderColor: ORANGE,
//               fontWeight: 700,
//             }}
//           >
//             {editData
//               ? "Update Fee Structure"
//               : "Create Fee Structure"}
//           </Button>
//         </Form>
//       </Modal>

//       <style>
//         {`
//           .professional-fee-row:hover td {
//             background: #fff3e8 !important;
//           }

//           .ant-table-thead > tr > th {
//             background: #111111 !important;
//             color: #ffffff !important;
//             font-weight: 700;
//             white-space: nowrap;
//           }

//           .ant-table-tbody > tr > td {
//             white-space: nowrap;
//             font-size: 14px;
//           }

//           .ant-card-head-title {
//             font-weight: 800;
//           }

//           @media (max-width: 768px) {
//             .ant-card {
//               border-radius: 14px !important;
//             }

//             .ant-table {
//               font-size: 13px;
//             }

//             .ant-statistic-content {
//               font-size: 22px !important;
//             }
//           }
//         `}
//       </style>
//     </Layout>
//   );
// };

// export default SchoolFeesData;

